import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { SOURCE_LABEL, errorMessage, fileToBase64, pesos } from "../../lib/renterDrivers";
import DriverDetailsForm, { emptyDetails, toPayload } from "./DriverDetailsForm";

// Task 6: the renter's view of who may drive. Works for a signed-in customer
// (basePath "/customer/bookings/:id") and a guest holding their booking link
// (basePath "/public/confirm/:token"). Shows plain-language status and what is
// needed — never staff notes, rule ids or anyone else's documents.
export default function CustomerDriversCard({ basePath, renterName, readOnly = false }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null); // "add" | "invite"
  const load = useCallback(() => {
    api.get(`${basePath}/drivers`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [basePath]);
  useEffect(() => { load(); }, [load]);
  if (!data || !data.applicable) return null;

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); setMode(null); load(); } catch (err) { toast.error(errorMessage(err)); }
  };
  const drivers = [data.primary, ...(data.additional || [])].filter(Boolean);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3" data-testid="customer-drivers-card">
      <h2 className="font-semibold">Authorized drivers</h2>
      <p className="text-sm text-slate-700">{data.unauthorized_driver_rule}</p>
      {!data.primary && <p className="text-sm text-amber-800">Tell us who will drive before pickup.</p>}
      <ul className="space-y-2">
        {drivers.map((d) => (
          <li key={d.authorized_driver_id} className="rounded-lg border border-slate-200 p-3" data-testid={`customer-driver-${d.authorized_driver_id}`}>
            <div className="flex flex-wrap justify-between gap-2">
              <span className="font-medium">{d.name || "Invited driver"} <span className="text-xs text-slate-500">({d.role === "PRIMARY" ? "Primary" : "Additional"})</span></span>
              <span className="text-xs font-medium">{d.invitation && d.status_code === "INVITED" ? d.invitation : d.status}</span>
            </div>
            {d.actions_needed.length > 0 && (
              <ul className="mt-1 text-xs text-slate-700 list-disc pl-4">{d.actions_needed.map((m) => <li key={m}>{m}</li>)}</ul>
            )}
            {!readOnly && (
              <div className="mt-2 flex flex-wrap gap-2">
                {d.can_upload && <UploadLicence basePath={basePath} driverId={d.authorized_driver_id} onDone={load} />}
                {d.role === "ADDITIONAL" && (
                  <button className="text-xs underline" onClick={() => act(() => api.post(`${basePath}/drivers/${d.authorized_driver_id}/remove`,
                    { reason: "Removed by the renter" }), "Driver removed")}>Remove</button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      {data.fees?.additional_driver_fee_centavos ? (
        <p className="text-sm">Driver fees: {pesos(data.fees.additional_driver_fee_centavos)}</p>
      ) : null}
      {data.requirements?.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer">Driver requirements for this rental</summary>
          <ul className="mt-1 space-y-0.5">
            {data.requirements.map((r) => <li key={r.key}>{r.text} <span className="text-xs text-slate-500">— {SOURCE_LABEL[r.source]}</span></li>)}
          </ul>
        </details>
      )}
      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          <button className="px-3 py-1.5 rounded-lg border text-sm" onClick={() => setMode("add")} data-testid="customer-add-driver">
            {data.primary ? "Add a driver" : "Add who will drive"}</button>
          <button className="px-3 py-1.5 rounded-lg border text-sm" onClick={() => setMode("invite")}>Invite a driver</button>
        </div>
      )}
      {mode === "add" && <AddForm basePath={basePath} primary={!data.primary} renterName={renterName}
                                  privacy={data.privacy_notice} onDone={() => { setMode(null); load(); }} />}
      {mode === "invite" && <InviteForm basePath={basePath} onDone={() => { setMode(null); load(); }} />}
      <p className="text-xs text-slate-500">{data.pickup_note}</p>
    </section>
  );
}

function AddForm({ basePath, primary, renterName, privacy, onDone }) {
  const [renterDriving, setRenterDriving] = useState(true);
  const [details, setDetails] = useState(emptyDetails(primary ? renterName || "" : ""));
  const [ack, setAck] = useState(false);
  const submit = async () => {
    try {
      await api.post(`${basePath}/drivers`, { role: primary ? "PRIMARY" : "ADDITIONAL", renter_is_driver: primary && renterDriving,
                                              details: toPayload(details), privacy_acknowledged: ack });
      toast.success("Driver added. Likely eligible — the licence still needs review.");
      onDone();
    } catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <div className="rounded-lg border border-slate-300 p-3 space-y-3" role="dialog" aria-label="Add driver" data-testid="customer-add-form">
      {primary && (
        <fieldset className="text-sm space-y-1">
          <legend className="font-medium">Who will drive?</legend>
          <label className="flex items-center gap-2"><input type="radio" checked={renterDriving}
                 onChange={() => { setRenterDriving(true); setDetails({ ...details, legal_name: renterName || "" }); }} /> I will drive</label>
          <label className="flex items-center gap-2"><input type="radio" checked={!renterDriving}
                 onChange={() => { setRenterDriving(false); setDetails({ ...details, legal_name: "" }); }} /> Someone else will be the primary driver</label>
        </fieldset>
      )}
      <DriverDetailsForm value={details} onChange={setDetails} idPrefix="customer-add" />
      <label className="flex items-start gap-2 text-xs"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
        <span>{privacy}</span></label>
      <button disabled={!ack || !details.legal_name.trim()} onClick={submit} className="px-3 py-1.5 rounded-lg btn-brand text-sm disabled:opacity-50">Save driver</button>
    </div>
  );
}

function InviteForm({ basePath, onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const submit = async () => {
    try { await api.post(`${basePath}/invitations`, { name, email }); toast.success("Invitation sent."); onDone(); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <div className="rounded-lg border border-slate-300 p-3 space-y-2" role="dialog" aria-label="Invite driver">
      <p className="text-xs text-slate-600">They enter their own licence details through a private link. You will see their status, not their documents.</p>
      <label className="block text-sm"><span className="text-xs">Name</span><input className="mt-1 w-full px-3 py-2 rounded-lg border" value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="block text-sm"><span className="text-xs">Email</span><input type="email" className="mt-1 w-full px-3 py-2 rounded-lg border" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
      <button disabled={!name || !email} onClick={submit} className="px-3 py-1.5 rounded-lg btn-brand text-sm disabled:opacity-50">Send invitation</button>
    </div>
  );
}

function UploadLicence({ basePath, driverId, onDone }) {
  const upload = async (file) => {
    if (!file) return;
    try {
      await api.post(`${basePath}/drivers/${driverId}/evidence`, {
        kind: "LICENCE_FRONT", data_base64: await fileToBase64(file), content_type: file.type || "application/pdf",
        filename: file.name, idempotency_key: `${driverId}-${file.name}-${file.size}` });
      toast.success("Licence uploaded. The rental business will review it.");
      onDone();
    } catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <label className="text-xs underline cursor-pointer">
      Upload licence photo
      <input type="file" className="sr-only" accept="image/jpeg,image/png,image/webp,application/pdf"
             onChange={(e) => upload(e.target.files?.[0])} />
    </label>
  );
}
